<template>
  <div class="section-list">
    <div class="section-list__header">
      <span class="section-list__title">
        セクション
        <el-tooltip placement="top">
          <div slot="content">
            カスタムレイアウトでは、複数の画像セクション（最大10個）と1つのボタン・規約セクションを自由に配置できます。
          </div>
          <i class="mdi mdi-help-circle"></i>
        </el-tooltip>
      </span>
      <el-button type="text" :disabled="isAddDisabled" @click="handleAddSection">
        <i class="el-icon-plus" />
        セクション追加
      </el-button>
    </div>

    <div class="section-list__items">
      <div
        v-for="(section, index) in internalSections"
        :key="getSectionKey(section, index)"
        class="section-list__item"
        :class="{ 'section-list__item--drag-over': dragOverIndex === index }"
        :draggable="!disabled"
        @dragstart="handleDragStart($event, index)"
        @dragover.prevent="handleDragOver(index)"
        @dragleave="handleDragLeave"
        @drop="handleDrop(index)"
        @dragend="handleDragEnd"
      >
        <SectionImage
          v-if="section.mode === 'media'"
          :section="section"
          :body-background-color="bodyBackgroundColor"
          :collapsed="!expandedKeys.includes(getSectionKey(section, index))"
          :can-delete="mediaCount > 1 && !disabled"
          :disabled="disabled"
          :showHex="showHex"
          @update="handleSectionUpdate(index, $event)"
          @toggle="toggleSection(getSectionKey(section, index))"
          @delete="handleDeleteSection(index)"
          @media-change="handleMediaChange(index, $event)"
          @media-remove="handleMediaRemove(index, $event)"
        />
        <SectionTerms
          v-if="section.mode === 'rule'"
          :section="section"
          :collapsed="!expandedKeys.includes(getSectionKey(section, index))"
          :disabled="disabled"
          :showHex="showHex"
          @update="handleSectionUpdate(index, $event)"
          @toggle="toggleSection(getSectionKey(section, index))"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import SectionImage from '@/client/components/_molecules/section_image'
import SectionTerms from '@/client/components/_molecules/section_terms'

import {
  appendMediaSection,
  getInitiallyExpandedMediaIndexes,
  moveSection,
  normalizeSectionOrder,
  removeSectionAt,
  WebInstantwinCustomSection
} from './section_list.utils'

export default defineComponent({
  name: 'SectionList',
  components: {
    SectionImage,
    SectionTerms
  },
  props: {
    sections: {
      type: Array as PropType<WebInstantwinCustomSection[]>,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showHex: {
      type: Boolean,
      default: false
    },
    bodyBackgroundColor: {
      type: String,
      default: '#ffffff'
    }
  },
  emits: ['update', 'media-change', 'media-remove'],
  setup(props, { emit }) {
    const internalSections = ref<WebInstantwinCustomSection[]>([...props.sections])
    const expandedKeys = ref<string[]>([])
    const hasInitialized = ref(false)
    const dragIndex = ref<number | null>(null)
    const dragOverIndex = ref<number | null>(null)

    const mediaCount = computed(() => internalSections.value.filter(section => section.mode === 'media').length)
    const maxAddedSections = 11
    const isAddDisabled = computed(() => props.disabled || internalSections.value.length >= maxAddedSections)

    const getSectionKey = (section: WebInstantwinCustomSection, index: number) =>
      `${section.section_id ?? 'temp'}-${section.mode}-${section.step_order}-${index}`

    watch(
      () => props.sections,
      newValue => {
        internalSections.value = [...newValue]
        if (!hasInitialized.value && newValue.length > 0) {
          expandedKeys.value = getInitiallyExpandedMediaIndexes(newValue).map(index =>
            getSectionKey(newValue[index], index)
          )
          hasInitialized.value = true
        }
      },
      { deep: true, immediate: true }
    )

    const toggleSection = (key: string) => {
      const idx = expandedKeys.value.indexOf(key)
      if (idx >= 0) {
        expandedKeys.value.splice(idx, 1)
      } else {
        expandedKeys.value.push(key)
      }
    }

    const emitUpdate = (sections: WebInstantwinCustomSection[]) => {
      const normalized = normalizeSectionOrder(sections)
      internalSections.value = normalized
      emit('update', normalized)
    }

    const handleAddSection = () => {
      if (isAddDisabled.value) {
        return
      }

      const next = appendMediaSection(internalSections.value)
      emitUpdate(next)
      hasInitialized.value = true
    }

    const handleDeleteSection = (index: number) => {
      if (props.disabled) {
        return
      }

      const next = removeSectionAt(internalSections.value, index)
      emitUpdate(next)
    }

    const handleSectionUpdate = (index: number, updated: WebInstantwinCustomSection) => {
      const next = [...internalSections.value]
      next[index] = updated
      emitUpdate(next)
    }

    const handleMediaChange = (index: number, payload: { field: 'pc' | 'sp'; file: File }) => {
      emit('media-change', { index, ...payload })
    }

    const handleMediaRemove = (index: number, payload: { field: 'pc' | 'sp' }) => {
      emit('media-remove', { index, ...payload })
    }

    const handleDragStart = (_event: DragEvent, index: number) => {
      if (props.disabled) {
        return
      }
      dragIndex.value = index
    }

    const handleDragOver = (index: number) => {
      if (props.disabled) {
        return
      }
      dragOverIndex.value = index
    }

    const handleDragLeave = () => {
      dragOverIndex.value = null
    }

    const handleDrop = (index: number) => {
      if (props.disabled) {
        return
      }

      if (dragIndex.value === null || dragIndex.value === index) {
        return
      }

      const next = moveSection(internalSections.value, dragIndex.value, index)
      dragOverIndex.value = null
      emitUpdate(next)
    }

    const handleDragEnd = () => {
      dragIndex.value = null
      dragOverIndex.value = null
    }

    return {
      internalSections,
      expandedKeys,
      dragOverIndex,
      mediaCount,
      isAddDisabled,
      getSectionKey,
      toggleSection,
      handleAddSection,
      handleDeleteSection,
      handleSectionUpdate,
      handleMediaChange,
      handleMediaRemove,
      handleDragStart,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleDragEnd
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.mdi-help-circle {
  margin-left: $spacing-unit;
  font-size: 18px;
}

.section-list {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $spacing-unit * 2;
  }

  &__title {
    display: flex;
    gap: $spacing-unit;
    align-items: center;
    font-size: $font-size;
    font-weight: bold;
    color: $text-color;
  }

  &__tooltip-icon {
    color: $sub-text-color;
    cursor: help;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: $spacing-unit;
  }

  &__item {
    transition: opacity 0.2s;

    &--drag-over {
      border-top: 2px solid $primary-color;
      opacity: 0.5;
    }
  }
}
</style>
